import React, { useState, useEffect, useRef, startTransition } from 'react';
import { useTranslation, Trans } from 'react-i18next';

interface FaqItem {
  question: string;
  answer: string;
}

const FAQSection: React.FC = () => {
  const { t } = useTranslation();
  const [showScroll, setShowScroll] = useState(false);
  const scrollRef = useRef<HTMLUListElement>(null);

  const faqItems = t('faq.items', { returnObjects: true }) as FaqItem[];

  useEffect(() => {
    const checkOverflow = () => {
      if (scrollRef.current) {
        const { scrollHeight, clientHeight } = scrollRef.current;
        startTransition(() => setShowScroll(scrollHeight > clientHeight));
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [faqItems]);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        setShowScroll(false);
      } else {
        setShowScroll(true);
      }
    }
  };

  return (
    <section className='faqSection'>
      <div className='faqCard'>
        <div className='faqHeader'>
          <div className='faqHeaderContent'>
            <h2 className='faqTitle'>{t('faq.header')}</h2>
            <p className='faqDescription'>{t('faq.description')}</p>
          </div>
        </div>
        <div className='faqListWrapper'>
          <ul className='faqList' ref={scrollRef} onScroll={handleScroll}>
            {faqItems.map((item) => (
              <li key={item.question} className='faqItem'>
                <span className='faqIcon'>✔</span>
                <div className='faqContent'>
                  <strong className='faqQuestion'>{item.question}</strong>
                  <span className='faqAnswer'>
                    {
                      <Trans
                        i18nKey={item.answer}
                        components={{
                          a: <a />,
                        }}
                      />
                    }
                  </span>
                </div>
              </li>
            ))}
          </ul>
          {showScroll && <div className='scrollIndicator'></div>}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
